import React, { useEffect, useMemo, useState } from "react";
import LibraryPeriodSelect from "./LibraryPeriodSelect";
import RfpJobRow from "./RfpJobRow";
import RfpLibraryModal from "./RfpLibraryModal";
import RfpUploadZone from "./RfpUploadZone";
import { isRfpExcelFile } from "./RfpUploadZone";
import { RFP_COUNTRIES, RFP_OWNER_SES, RFP_VERSIONS } from "./rfpUploadFields";
import {
  displayCountry,
  displayCustomer,
  displayOwnerSe,
  filterLibraryJobs,
  LIBRARY_DEFAULT_VISIBLE,
  LIBRARY_ROW_GRID,
  sortLibraryJobsByRecency,
  uniqueCountries,
  uniqueOwnerSes,
} from "./rfpUtils";

/**
 * Compact RFP selection / creation shell.
 * Modes: existing | new | collapsed (working summary bar).
 */
export default function RfpSelector({
  jobs,
  activeJobId,
  jobMeta,
  onOpenJob,
  libraryPeriod = "12m",
  availableYears = [],
  onLibraryPeriodChange,
  // upload props
  file,
  onFile,
  customerName,
  setCustomerName,
  ownerSe,
  setOwnerSe,
  country,
  setCountry,
  email,
  setEmail,
  pageRanges,
  setPageRanges,
  pageScope,
  setPageScope,
  rfpVersion,
  setRfpVersion,
  uploadTouched,
  setUploadTouched,
  customerValid,
  ownerSeValid,
  countryValid,
  fileValid,
  pageRangesValid,
  canUpload,
  uploadBlocked,
  busy,
  activeJobs,
  onUpload,
  fieldCls,
  selectorRef,
}) {
  const [mode, setMode] = useState(() => (activeJobId ? "collapsed" : "new"));
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Enter working/collapsed mode when a job is selected (URL or after upload).
  useEffect(() => {
    if (activeJobId) setMode("collapsed");
    else if (mode === "collapsed") setMode("new");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to job presence
  }, [activeJobId]);

  // Allow parent to force reopen via selectorRef
  useEffect(() => {
    if (!selectorRef) return undefined;
    selectorRef.current = {
      reopenExisting: () => setMode("existing"),
      openNew: () => setMode("new"),
    };
    return () => {
      selectorRef.current = null;
    };
  }, [selectorRef]);

  const isExcelUpload = isRfpExcelFile(file);

  const ownerOptions = useMemo(() => uniqueOwnerSes(jobs), [jobs]);
  const countryOptions = useMemo(() => uniqueCountries(jobs), [jobs]);
  const filtered = useMemo(
    () => filterLibraryJobs(jobs, { search, ownerSe: ownerFilter, country: countryFilter }),
    [jobs, search, ownerFilter, countryFilter]
  );
  const sortedFiltered = useMemo(() => sortLibraryJobsByRecency(filtered), [filtered]);
  const visibleJobs = sortedFiltered.slice(0, LIBRARY_DEFAULT_VISIBLE);
  const hasMore = sortedFiltered.length > LIBRARY_DEFAULT_VISIBLE;

  const filterCls =
    "rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950";

  const tabCls = (active) =>
    `rounded-lg px-3 py-1.5 text-sm font-medium transition ${
      active
        ? "bg-emerald-700 text-white shadow-sm"
        : "border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
    }`;

  if (mode === "collapsed" && activeJobId) {
    const summaryMeta = jobMeta || jobs.find((j) => j.job_id === activeJobId) || {};
    return (
      <section className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">RFP</p>
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {displayCustomer(summaryMeta)}
              <span className="font-normal text-slate-500 dark:text-slate-400">
                {" "}
                · {displayCountry(summaryMeta)} · {displayOwnerSe(summaryMeta)}
              </span>
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={() => setMode("existing")}
            data-testid="rfp-change-rfp"
          >
            Change RFP
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">RFP</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={tabCls(mode === "new")} onClick={() => setMode("new")}>
            + New RFP
          </button>
          <button type="button" className={tabCls(mode === "existing")} onClick={() => setMode("existing")}>
            Existing RFP
          </button>
        </div>
      </div>

      {mode === "existing" ? (
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <LibraryPeriodSelect
              period={libraryPeriod}
              years={availableYears}
              onChange={onLibraryPeriodChange}
              className={filterCls}
            />
            <input
              type="search"
              placeholder="Search customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${filterCls} min-w-[10rem] flex-1`}
              data-testid="rfp-selector-search"
            />
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className={filterCls}
              aria-label="Filter by Owner SE"
            >
              <option value="">Owner SE</option>
              {ownerOptions.map((se) => (
                <option key={se} value={se}>{se}</option>
              ))}
            </select>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className={filterCls}
              aria-label="Filter by Country"
            >
              <option value="">Country</option>
              {countryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className={`mb-1 hidden gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:grid ${LIBRARY_ROW_GRID}`}>
            <span>Customer</span>
            <span>Country</span>
            <span>Owner SE</span>
            <span>Updated</span>
            <span className="justify-self-end">Status</span>
            <span className="justify-self-end">Downloads</span>
          </div>

          {sortedFiltered.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500 dark:border-slate-700">
              {jobs.length === 0 ? "No RFP jobs yet. Create one with + New RFP." : "No jobs match your search."}
            </p>
          ) : (
            <div data-testid="rfp-library-list" className="divide-y divide-slate-100 dark:divide-slate-800">
              {visibleJobs.map((job) => (
                <RfpJobRow
                  key={job.job_id}
                  job={job}
                  active={job.job_id === activeJobId}
                  onOpen={(id) => {
                    onOpenJob(id);
                    setMode("collapsed");
                  }}
                />
              ))}
            </div>
          )}

          {hasMore && (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 dark:text-emerald-300"
              onClick={() => setViewAllOpen(true)}
              data-testid="rfp-library-view-all"
            >
              View all {sortedFiltered.length} →
            </button>
          )}
        </div>
      ) : (
        <div>
          {uploadBlocked && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
              <span className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
              <span>
                Pipeline busy
                {activeJobs[0]?.filename ? ` — “${activeJobs[0].filename}”` : ""}
                {activeJobs[0]?.status ? ` (${activeJobs[0].status})` : ""}.
              </span>
            </div>
          )}

          <form className="space-y-2.5" onSubmit={onUpload}>
            <div className="grid gap-2.5 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Owner SE <span className="text-red-600">*</span>
                </span>
                <select
                  required
                  value={ownerSe}
                  onChange={(e) => setOwnerSe(e.target.value)}
                  onBlur={() => setUploadTouched(true)}
                  disabled={uploadBlocked}
                  className={fieldCls}
                >
                  <option value="">Select Owner SE</option>
                  {RFP_OWNER_SES.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {uploadTouched && !ownerSeValid && (
                  <span className="text-xs text-red-600 dark:text-red-400">Owner SE is required.</span>
                )}
              </label>

              <label className="flex flex-col gap-1 text-sm sm:col-span-1">
                <span className="text-slate-600 dark:text-slate-300">
                  Customer <span className="text-red-600">*</span>
                </span>
                <input
                  type="text"
                  required
                  placeholder="as appears in CRM"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  onBlur={() => setUploadTouched(true)}
                  disabled={uploadBlocked}
                  className={fieldCls}
                />
                {uploadTouched && !customerValid && (
                  <span className="text-xs text-red-600 dark:text-red-400">Customer name is required.</span>
                )}
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Country <span className="text-red-600">*</span>
                </span>
                <select
                  required
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  onBlur={() => setUploadTouched(true)}
                  disabled={uploadBlocked}
                  className={fieldCls}
                >
                  <option value="">Select country</option>
                  {RFP_COUNTRIES.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {uploadTouched && !countryValid && (
                  <span className="text-xs text-red-600 dark:text-red-400">Country is required.</span>
                )}
              </label>
            </div>

            <div>
              <span className="mb-1 block text-sm text-slate-600 dark:text-slate-300">
                Document <span className="text-red-600">*</span>
              </span>
              <RfpUploadZone file={file} onFile={onFile} disabled={uploadBlocked} busy={busy} compact />
              {uploadTouched && !fileValid && (
                <span className="mt-1 block text-xs text-red-600 dark:text-red-400">PDF, Word (.docx), or Excel file is required.</span>
              )}
            </div>

            {!isExcelUpload && (
            <fieldset className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <legend className="px-1 text-sm text-slate-600 dark:text-slate-300">
                Pages to process <span className="text-red-600">*</span>
              </legend>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="radio"
                    name="page-scope"
                    value="entire_document"
                    checked={pageScope === "entire_document"}
                    onChange={(e) => {
                      setPageScope(e.target.value);
                      setPageRanges("");
                    }}
                    disabled={uploadBlocked}
                    required
                  />
                  Entire document
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="radio"
                    name="page-scope"
                    value="specific_pages"
                    checked={pageScope === "specific_pages"}
                    onChange={(e) => setPageScope(e.target.value)}
                    disabled={uploadBlocked}
                    required
                  />
                  Specific pages
                </label>
              </div>
              {pageScope === "specific_pages" && (
                <label className="mt-3 flex max-w-md flex-col gap-1 text-sm">
                  <span className="text-slate-600 dark:text-slate-300">Page ranges</span>
                  <input
                    type="text"
                    placeholder="e.g. 90-100, 405-600"
                    value={pageRanges}
                    onChange={(e) => setPageRanges(e.target.value)}
                    onBlur={() => setUploadTouched(true)}
                    disabled={uploadBlocked}
                    className={fieldCls}
                    required
                  />
                  {uploadTouched && !pageRangesValid && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      Enter at least one range, using formats like 90-100 or 405.
                    </span>
                  )}
                </label>
              )}
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Smaller page ranges improve precision, cost less, and finish faster. Select only the pages you need when possible.
                For Word files, use the page numbers in Word's status bar (Print Layout), the same as the PDF page numbers.
              </p>
              {uploadTouched && !pageScope && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Select a page option.</p>
              )}
            </fieldset>
            )}

            {isExcelUpload && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                After upload you will choose which worksheets to analyze.
              </p>
            )}

            <button
              type="button"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
            >
              More options {moreOpen ? "▴" : "▾"}
            </button>

            {moreOpen && (
              <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-950/40">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Optional fields: notification email and RFP version (v1–v5).
                </p>
                <div className="grid gap-2.5 sm:grid-cols-3">
                  <label className="flex flex-col gap-1 text-sm sm:col-span-1">
                    <span className="text-slate-600 dark:text-slate-300">Notification email</span>
                    <input
                      type="email"
                      placeholder="Leave empty for no notification"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={uploadBlocked}
                      className={fieldCls}
                    />
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Send a completion email when processing finishes. Leave blank for no notification.
                    </span>
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-slate-600 dark:text-slate-300">RFP version</span>
                    <select
                      value={rfpVersion}
                      onChange={(e) => setRfpVersion(e.target.value)}
                      disabled={uploadBlocked}
                      className={fieldCls}
                    >
                      <option value="">No version</option>
                      {RFP_VERSIONS.map((ver) => (
                        <option key={ver} value={ver}>{ver}</option>
                      ))}
                    </select>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Document revision label for this RFP (v1–v5). Optional.
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={!canUpload || uploadBlocked}
                className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60"
              >
                {busy ? "Uploading…" : uploadBlocked ? "Upload disabled" : "Upload & process"}
              </button>
            </div>
          </form>
        </div>
      )}

      <RfpLibraryModal
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        jobs={jobs}
        activeJobId={activeJobId}
        libraryPeriod={libraryPeriod}
        availableYears={availableYears}
        onLibraryPeriodChange={onLibraryPeriodChange}
        onOpen={(id) => {
          onOpenJob(id);
          setMode("collapsed");
        }}
      />
    </section>
  );
}
